import React, { useState } from "react";
import { PaymentForm, CreditCard } from "react-square-web-payments-sdk";
import { createSquarePayment } from "../services/api";
import toast from "react-hot-toast";
import { activeCurrencySymbol, activeCurrencyCode } from "../utils/currency";

interface SquarePaymentProps {
  pricing: number;
  handleOrder: (paymentMethod: string, transactionId?: string) => void;
  onSuccess?: () => void;
  // The restaurant's own Square account (from its payment options).
  restaurantId: string;
  applicationId: string;
  locationId: string;
  countryCode?: string;
}

export default function SquarePayment({
  pricing,
  handleOrder,
  onSuccess,
  restaurantId,
  applicationId: squareApplicationId,
  locationId: squareLocationId,
  countryCode = "US",
}: SquarePaymentProps) {
  const [loading, setLoading] = useState(false);

  const paymentAmountCents = Math.round(pricing * 100);
  const currencyCode = activeCurrencyCode();
  const paymentLabel = "Order Total";

  // Validate Square credentials
  const isValidCredential = (id: string | null): boolean => {
    if (!id || typeof id !== "string") return false;
    const trimmed = id.trim();
    return trimmed.length > 0 && trimmed !== "undefined" && trimmed !== "null";
  };

  // Don't render if Square credentials are not available
  if (
    !isValidCredential(squareApplicationId) ||
    !isValidCredential(squareLocationId)
  ) {
    console.log("Square credentials:", {
      squareApplicationId,
      squareLocationId,
    });
    return (
      <div className="w-full bg-white rounded-lg p-4">
        <p className="text-gray-500 text-center">Loading payment form...</p>
      </div>
    );
  }

  return (
    <div className="w-full bg-white rounded-lg p-4">
      <PaymentForm
        key="square-payment-form"
        applicationId={squareApplicationId!.trim()}
        locationId={squareLocationId!.trim()}
        createPaymentRequest={() => ({
          countryCode,
          currencyCode,
          total: {
            amount: (paymentAmountCents / 100).toFixed(2),
            label: paymentLabel,
          },
        })}
        cardTokenizeResponseReceived={async (tokenResult) => {
          console.log("Token Result:", tokenResult);
          if (tokenResult.status !== "OK") {
            toast.error("Card tokenization failed. Please try again.");
            return;
          }

          try {
            setLoading(true);

            const res = await createSquarePayment({
              token: tokenResult.token!,
              amount: paymentAmountCents,
              hotelId: restaurantId,
            });

            console.log("Payment Response:", res.data);
            console.log("Extracted payment ID:", res.data?.data?.payment?.id);
            if (res.data.success) {
              const paymentId = res.data?.data?.payment?.id;
              console.log("Calling handleOrder with paymentId:", paymentId);
              toast.success("Payment successful!");
              handleOrder("square", paymentId);
              onSuccess?.();
            } else {
              toast.error(
                "Payment failed: " + (res.data.error || "Unknown error"),
              );
            }
          } catch (err: any) {
            console.error("Payment error:", err);
            toast.error(
              err.response?.data?.error || "Payment processing failed",
            );
          } finally {
            setLoading(false);
          }
        }}
      >
        <CreditCard
          includeInputLabels
          buttonProps={{
            css: {
              backgroundColor: "var(--main-color, #000)",
              color: "#fff",
              fontSize: "15px",
              fontWeight: "600",
              border: "none",
              borderRadius: "0.375rem",
              padding: "0.75rem",
              cursor: "pointer",
              width: "100%",
              marginTop: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              "&:hover": {
                backgroundColor: "var(--main-color, #000)",
                opacity: 0.95,
              },
              "&:disabled": {
                backgroundColor: "#9ca3af",
                cursor: "not-allowed",
              },
            },
          }}
        >
          {loading ? (
            <>
              <svg
                style={{
                  width: "1.25rem",
                  height: "1.25rem",
                  animation: "spin 1s linear infinite",
                  color: "white",
                }}
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Processing...
            </>
          ) : (
            `PAY ${activeCurrencySymbol()}${pricing.toFixed(2)}`
          )}
        </CreditCard>
      </PaymentForm>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        #sq-card-number,
        #sq-cvv,
        #sq-expiration-date,
        #sq-postal-code {
          background: white !important;
          border: 1px solid rgba(0, 0, 0, 0.15) !important;
          border-radius: 8px !important;
          padding: 14px !important;
          font-size: 15px !important;
          transition: border-color 0.2s, box-shadow 0.2s !important;
          height: 50px !important;
        }

        #sq-card-number:focus,
        #sq-cvv:focus,
        #sq-expiration-date:focus,
        #sq-postal-code:focus {
          border-color: var(--main-color, #000) !important;
          box-shadow: 0 0 0 1px var(--main-color, #000) !important;
          outline: none !important;
        }

        .sq-input--exp,
        .sq-input--cvv,
        .sq-input--postal {
          display: inline-block !important;
          width: calc(33.333% - 8px) !important;
          margin-right: 12px !important;
        }

        .sq-input--postal {
          margin-right: 0 !important;
        }

        .sq-label {
          color: #4b5563 !important;
          font-size: 13px !important;
          font-weight: 600 !important;
          margin-bottom: 8px !important;
          display: block !important;
        }

        .sq-error-message {
          color: #ef4444 !important;
          font-size: 12px !important;
          margin-top: 6px !important;
          font-weight: 500 !important;
        }

        .sq-input {
          margin-bottom: 18px !important;
        }

        .sq-input--card-number {
          margin-bottom: 18px !important;
        }

        @media (max-width: 640px) {
          .sq-input--exp,
          .sq-input--cvv,
          .sq-input--postal {
            width: 100% !important;
            margin-right: 0 !important;
          }
        }
      `}</style>
    </div>
  );
}
